import { useEffect, useRef, useState } from "react";

type UseIntersectionObserverOptions = {
	threshold?: number;
	rootMargin?: string;
	triggerOnce?: boolean;
};

export function useIntersectionObserver(options: UseIntersectionObserverOptions = {}) {
	const { threshold = 0.2, rootMargin = "0px", triggerOnce = true } = options;
	const [isIntersecting, setIsIntersecting] = useState(false);
	const [hasIntersected, setHasIntersected] = useState(false);
	const elementRef = useRef<HTMLElement>(null);

	useEffect(() => {
		const element = elementRef.current;
		if (!element) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				const isVisible = entry.isIntersecting;
				setIsIntersecting(isVisible);

				if (isVisible && !hasIntersected) {
					setHasIntersected(true);
				}
			},
			{ threshold, rootMargin },
		);

		observer.observe(element);

		return () => {
			observer.unobserve(element);
		};
	}, [threshold, rootMargin, hasIntersected]);

	const shouldAnimate = triggerOnce ? hasIntersected : isIntersecting;

	return { ref: elementRef, isVisible: shouldAnimate };
}
